import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../translations/translations';

// Render an answer that may contain the {servicesLink} placeholder by splitting
// around it and inserting an in-app <Link> to /services.
const renderAnswer = (answer, linkLabel) => {
  const token = '{servicesLink}';
  if (!answer.includes(token)) return answer;
  const [before, after] = answer.split(token);
  return (
    <>
      {before}
      <Link
        to="/services"
        className="text-blue-900 underline hover:text-blue-700 font-medium"
        data-testid="faq-pricing-link"
      >
        {linkLabel}
      </Link>
      {after}
    </>
  );
};

const FAQ = () => {
  const { language } = useLanguage();

  const faqItems = [
    { id: 'q1', question: t(language, 'faqPage.q1'), answer: t(language, 'faqPage.a1') },
    { id: 'q2', question: t(language, 'faqPage.q2'), answer: t(language, 'faqPage.a2') },
    { id: 'q3', question: t(language, 'faqPage.q3'), answer: t(language, 'faqPage.a3') },
    { id: 'q4', question: t(language, 'faqPage.q4'), answer: t(language, 'faqPage.a4') },
    { id: 'q5', question: t(language, 'faqPage.q5'), answer: t(language, 'faqPage.a5') },
    { id: 'q6', question: t(language, 'faqPage.q6'), answer: t(language, 'faqPage.a6') },
    { id: 'q7', question: t(language, 'faqPage.q7'), answer: t(language, 'faqPage.a7') },
    { id: 'q8', question: t(language, 'faqPage.q8'), answer: t(language, 'faqPage.a8') },
    { id: 'q9', question: t(language, 'faqPage.q9'), answer: t(language, 'faqPage.a9') },
    { id: 'q10', question: t(language, 'faqPage.q10'), answer: t(language, 'faqPage.a10') },
    { id: 'q11', question: t(language, 'faqPage.q11'), answer: t(language, 'faqPage.a11') },
    {
      id: 'q12',
      question: t(language, 'faqPage.q12'),
      answer: t(language, 'faqPage.a12'),
      linkLabel: t(language, 'faqPage.a12LinkLabel'),
    },
    { id: 'q13', question: t(language, 'faqPage.q13'), answer: t(language, 'faqPage.a13') },
    { id: 'q14', question: t(language, 'faqPage.q14'), answer: t(language, 'faqPage.a14') },
  ];

  return (
    <div className="min-h-screen bg-[#fefdf4]">
      {/* Hero */}
      <section className="bg-[#fefdf4] py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">{t(language, 'pages.faqHero')}</h1>
          <p className="text-xl text-gray-600">
            {t(language, 'pages.faqSubtitle')}
          </p>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Accordion type="single" collapsible className="space-y-4">
            {faqItems.map((item) => (
              <AccordionItem
                key={item.id}
                value={item.id}
                className="bg-white border border-gray-200 rounded-lg px-6 hover:border-blue-900 transition-colors"
                data-testid={`faq-item-${item.id}`}
              >
                <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-blue-900 py-6">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 pb-6 leading-relaxed">
                  {renderAnswer(item.answer, item.linkLabel)}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Still Have Questions */}
      <section className="py-20 bg-[#f5f2e8]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">{t(language, 'faqPage.stillHaveQuestions')}</h2>
          <p className="text-lg text-gray-600 mb-8">
            {t(language, 'faqPage.stillHaveQuestionsDesc')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/start">
              <Button size="lg" className="shine-cta w-full sm:w-auto bg-blue-900 hover:bg-blue-800 text-white px-8">
                {t(language, 'nav.startCta')}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQ;
